import {
    createReducer,
    createAction,
    createAsyncThunk,
} from '@reduxjs/toolkit';

import { LOCALSTORAGE } from 'config';
import {
    AuthManager,
    SelfManager,
    Loadable,
    isErrorResp,
    ErrorTypes,
} from 'models';
import { makeThunkFromAPICall } from 'state';

export type AuthState = {
    tokens: Loadable<AuthManager.OAuth2Creds>;
    user: Loadable<SelfManager.User>;
};

const initialState: AuthState = {
    tokens: { status: 'idle' },
    user: { status: 'idle' },
};

export const setTokensStatus = createAction<Loadable<AuthManager.OAuth2Creds>>(
    'auth/set-tokens-status',
);

export const setTokens =
    createAction<AuthManager.OAuth2Creds>('auth/set-tokens');

export const [getSelf, setUser] = makeThunkFromAPICall(
    SelfManager.getSelf,
    'auth/set-user',
);

export const [updateSelf, setUpdateSelf] = makeThunkFromAPICall(
    SelfManager.updateSelf,
    'auth/user/update-self',
);

export const getToken = createAsyncThunk(
    'auth/get-token',
    async (
        payload: {
            authCode?: string;
            tokens?: AuthManager.OAuth2Creds | null;
        },
        thunk,
    ) => {
        thunk.dispatch(setTokensStatus({ status: 'loading' }));
        if (payload.authCode || payload.tokens?.refreshToken) {
            try {
                const creds = await AuthManager.getToken(payload);
                if (!isErrorResp(creds)) {
                    thunk.dispatch(
                        setTokensStatus({
                            object: creds,
                            status: 'finished',
                        }),
                    );
                    localStorage.setItem(
                        LOCALSTORAGE.AUTH,
                        JSON.stringify(creds),
                    );
                    return creds;
                }
                thunk.dispatch(
                    setTokensStatus({
                        error: creds,
                        status: 'error',
                    }),
                );
            } catch (err: any) {
                thunk.dispatch(
                    setTokensStatus({
                        error: err,
                        status: 'error',
                    }),
                );
            }
        } else {
            thunk.dispatch(
                setTokensStatus({
                    error: {
                        error: 'No credentials provided',
                        code: ErrorTypes.INVALID_CREDENTIALS,
                    },
                    status: 'error',
                }),
            );
        }
        return null;
    },
);

export const authReducer = createReducer(initialState, (builder) => {
    builder.addCase(setTokensStatus, (state, action) => {
        return { ...state, tokens: { ...state.tokens, ...action.payload } };
    });
    builder.addCase(setTokens, (state, action) => {
        return {
            ...state,
            tokens: { ...state.tokens, object: action.payload },
        };
    });
    builder.addCase(setUser, (state, action) => {
        return {
            ...state,
            user: { ...state.user, ...action.payload.loadable },
        };
    });

    builder.addCase(setUpdateSelf, (state, action) => {
        return {
            ...state,
            user: { ...state.user, ...action.payload.loadable },
        };
    });
});

export const AuthActions = {
    setTokens,
    setTokensStatus,
    setUser,
    getSelf,
    getToken,
    updateSelf,
};

export default authReducer;
